import { RefetchOptions, UseAxios } from 'axios-hooks';
import {
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosRequestTransformer,
  AxiosResponseTransformer,
  AxiosPromise,
  AxiosHeaders,
} from 'axios';
import { useEffect, useCallback, useState } from 'react';
import { useToken, useAsyncError } from '../../hooks';
import { isPresent } from 'Shared/utils';

export type LazytApiExecuteFuncType<T> = (
  config?: AxiosRequestConfig,
  options?: RefetchOptions
) => AxiosPromise<T>;

export type UseLazytApiType<T> = [
  {
    data: T;
    loading: boolean;
    error: AxiosError<any, any> | undefined;
  },
  LazytApiExecuteFuncType<T>,
  string | null
];

const jsonStringifyTransformRequest: AxiosRequestTransformer = (data: any) =>
  JSON.stringify(data);

const jsonParseTransformResponse: AxiosResponseTransformer = (data: any) =>
  typeof data !== 'string' ? data : JSON.parse(data);

const setupConfig = (
  token: string | null,
  config?: AxiosRequestConfig
): InternalAxiosRequestConfig => {
  const headers = new AxiosHeaders({
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    ...(config?.headers ?? {}),
    Authorization: `Bearer ${token}`,
  });
  const baseConfig = {
    ...config,
    responseType: config?.responseType || 'json',
    headers: headers,
  };

  let transformRequest: AxiosRequestTransformer[] | null;

  if (config?.transformRequest) {
    if (Array.isArray(config?.transformRequest)) {
      transformRequest = [
        ...config?.transformRequest,
        jsonStringifyTransformRequest,
      ];
    } else {
      transformRequest = [
        config?.transformRequest,
        jsonStringifyTransformRequest,
      ];
    }
  } else {
    transformRequest = null;
  }

  let transformResponse: AxiosResponseTransformer[] | null;

  if (config?.transformResponse) {
    if (Array.isArray(config?.transformResponse)) {
      transformResponse = [
        jsonParseTransformResponse,
        ...config?.transformResponse,
      ];
    } else {
      transformResponse = [
        jsonParseTransformResponse,
        config?.transformResponse,
      ];
    }
  } else {
    transformResponse = null;
  }
  const executeOptions = {
    ...baseConfig,
    ...(isPresent(transformRequest) ? { transformRequest } : {}),
    ...(isPresent(transformResponse) ? { transformResponse } : {}),
  };

  return executeOptions;
};

export const makeApiHooks = (useAxios: UseAxios) => {
  const useLazyApi = <T>(config: AxiosRequestConfig): UseLazytApiType<T> => {
    const token = useToken();
    const transformedConfig = setupConfig(token, config);
    const [jsonError, setJsonError] = useState<
      AxiosError<any, any> | undefined
    >();
    const [{ data, loading, error }, execute] = useAxios<T>(transformedConfig, {
      manual: true,
    });

    useEffect(() => {
      if (!error) return setJsonError(undefined);
      const err = error as AxiosError<any, any>;
      setJsonError({
        ...err,
        message: err.response
          ? err.response.data?.message ?? 'An error occurred'
          : err.message,
      });
    }, [error]);

    const executeCommand: UseLazytApiType<T>[1] = useCallback(
      async (execConfig?: AxiosRequestConfig, options?: RefetchOptions) => {
        return execute(setupConfig(token, execConfig), options);
      },
      [execute, token]
    );

    return [
      { data: data as T, loading: loading || !token, error: jsonError },
      executeCommand,
      token,
    ];
  };

  /* TODO: Need to deprecate this and use the useNewApi

     Main change is the url is part of the config, and you can send any
     Axios configuration to the hook vs. just the URL.
  */
  const useApi = <T>(url: string, shouldThrowError: boolean = true) => {
    const [isLoading, setIsLoading] = useState(true);
    const [{ data, loading, error }, execute, token] = useLazyApi<T>({
      url,
    });

    const throwError = useAsyncError();

    useEffect(() => {
      if (token) {
        execute({
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .catch(err => shouldThrowError && throwError(err))
          .finally(() => setIsLoading(false));
      }
    }, [token, execute, throwError, shouldThrowError]);

    return [{ data, loading: isLoading || loading, error, refetch: execute }];
  };

  const useNewApi = <T>(
    config: {
      url: string;
      baseURL?: string; // Added to override the baseURL for the api call and test in local
      transformResponse?: (data: any) => T;
      transformRequest?: (data: any) => unknown;
      timeout?: number;
    },
    useCache: boolean = false,
    shouldThrowError: boolean = true
  ) => {
    const [isLoading, setIsLoading] = useState(true);
    const [{ data, loading, error }, execute, token] = useLazyApi<T>({});

    const {
      url,
      transformResponse,
      transformRequest,
      timeout,
      baseURL,
    } = config;
    const throwError = useAsyncError();

    useEffect(() => {
      let isSubscribed = true;
      if (token) {
        execute(
          { url, transformRequest, transformResponse, timeout },
          { useCache }
        )
          .catch(err => shouldThrowError && throwError(err))
          .finally(() => {
            if (isSubscribed) {
              setIsLoading(false);
            }
          });
      }
      return () => {
        isSubscribed = false;
      };
    }, [
      token,
      execute,
      throwError,
      shouldThrowError,
      url,
      transformRequest,
      transformResponse,
      useCache,
      timeout,
      baseURL,
    ]);

    const refetch: LazytApiExecuteFuncType<T> = useCallback(
      newConfig => {
        return execute(newConfig ? newConfig : config);
      },
      [execute, config]
    );

    return [{ data, loading: isLoading || loading, error, refetch }];
  };

  return {
    useLazyApi,
    useApi,
    useNewApi,
  };
};
